// defense 탭의 신호 주인 한 줄 — **부호와 시제를 한 자리에서 고른다**
//
// ── 왜 이 파일이 따로 있는가 ────────────────────────────────────────────────
//
// 이 저장소가 같은 결함에 세 번 걸렸다. 코어의 형식기는 앞에 `T−` 를 붙이는데 그 뒤에
// 음수가 오면 `T−-1:07.7` 이 된다 — 빼기가 둘이라 오타로 읽히고, 더 나쁘게는 **라벨이
// 「개시까지」로 남아** 이미 지나간 순간을 화면이 「앞으로 올 것」이라고 말한다. 공격 열람이
// 2026-08-14에 그것으로 값을 깎였고 `formatSinceOpen` 이 그때 생겼다.
//
// **2026-08-16에 같은 결함이 세 번째로 났다.** 수비의 적 rally 행에는 그 규칙을 붙여
// 놓고 defense 탭의 신호 주인 줄에는 빠뜨렸다 — 대리 관측 2회차가 `T−-1:07.7` 을 표본
// 아홉에서 잡았다. **판단을 화면 안에 인라인으로 두면 화면이 늘 때마다 한 번씩 빠뜨린다.**
// 그래서 그 판단만 여기로 뺐고, 단위 검사가 경계에서 그것을 붙든다.
//
// ── 자르는 규칙은 여기 없다 ─────────────────────────────────────────────────
//
// 0.1초 칸으로 버리는 규칙은 열람 화면의 `live-clock.ts` 하나가 갖는다. 이 파일은 **둘 중
// 어느 쪽을 부를지와 어느 라벨을 붙일지**만 고른다. 자르는 규칙을 여기 베끼면 네 번째 벌이
// 생기고, 그때는 같은 4.99초가 화면마다 다른 글자가 된다.

import { formatRemaining, formatSinceOpen } from '../screens/view-attack/live-clock.ts'
import type { LiveSignalTarget } from '../signal/live-target.ts'
import type { OpenOutcome } from '../signal/open-outcome.ts'

/** 화면이 그대로 그릴 한 줄. 고른 것이 없으면 부르는 쪽이 `null` 을 받는다. */
export interface SignalLine {
  /**
   * 어느 문구를 쓸지. 개시 전은 `upcoming`(`composeSignalOwner`)과 `muted`(`composeSignalMuted`),
   * 개시 뒤는 `passed`(`composeSignalPassed`)와 그 셋의 꼬리 붙은 형이다.
   *
   * **`muted` 가 따로 있는 이유는 사람이 할 일이 다르기 때문이다.** 시간이 만든 상태에는 사람이
   * 할 것이 없지만, 껐다는 것은 손잡이를 다시 켜면 되는 상태다.
   *
   * **개시 뒤를 넷으로 가르는 이유** (2026-09-17 결정, 2026-10-04 개정, 사용자). 하나로 접으면 그
   * 줄이 앱이 못 울림, 제때 울림, 사람이 끔 셋 모두에 똑같아진다 (2026-09-04 대리 관측의 감점).
   * - `passedMuted`: 개시 순간에 꺼져 있었다. 「소리는 꺼져 있었습니다」. 현재형 「껐습니다」로
   *   두지 않는 것은 다시 켜면 이번 신호가 들릴 것처럼 읽히기 때문이다.
   * - `passedRang`: 제때 울렸다. 「소리는 제때 울렸습니다」.
   * - `passedMissed`: 고른 채 개시를 맞았는데 울리지 못했다. 「소리는 울리지 않았습니다」.
   * - `passed`: 화면이 개시 순간을 못 봤거나 아직 정하지 않았다. 꼬리를 붙이지 않는다.
   */
  readonly tense: 'upcoming' | 'muted' | 'passed' | 'passedMuted' | 'passedRang' | 'passedMissed'
  readonly rosterName: string
  /** 문구의 `{remaining}` 자리에 들어갈 글자. 부호와 방향이 이미 붙어 있다. */
  readonly remainingText: string
}

const PASSED_TENSE: Record<OpenOutcome, SignalLine['tense']> = {
  muted: 'passedMuted',
  rang: 'passedRang',
  missed: 'passedMissed',
}

/**
 * 지금 걸린 신호를 한 줄로 옮긴다. **개시를 지났으면 시제와 부호를 함께 뒤집는다.**
 *
 * 경계(`openMs === nowMs`)는 「지나가지 않았다」로 본다. 개시 그 순간은 아직 누를 수 있는
 * 순간이고, 그 한 틱을 「지났다」로 말하면 사람이 손을 멈춘다. 이 제품에서 늦음은 곧
 * rally 실패다. 같은 이유로 열람 화면의 카운트다운도 남은 시간을 과장하지 않는 쪽으로 자른다.
 *
 * 개시 뒤의 꼬리는 `target.outcome` 하나로 정한다. **지금 끔 여부로 대신하지 않는다**: 제때 울린
 * 뒤 끈 사람이나 개시 뒤에 고른 뒤 끈 사람에게 「꺼져 있었습니다」라는 거짓이 뜬다.
 */
export function signalLine(target: LiveSignalTarget | null, nowMs: number): SignalLine | null {
  if (target === null) return null
  const left = target.openMs - nowMs
  if (left < 0) {
    return {
      tense: target.outcome === null ? 'passed' : PASSED_TENSE[target.outcome],
      rosterName: target.rosterName,
      remainingText: formatSinceOpen(-left),
    }
  }
  // **껐음이 다가옴을 이긴다.** 아직 안 온 개시는 손잡이를 다시 켜면 들을 수 있으므로
  // 「껐다」가 지금 사람에게 쓸모 있는 사실이다. 지난 개시의 끔은 위에서 과거형으로 적는다.
  if (target.muted) {
    return { tense: 'muted', rosterName: target.rosterName, remainingText: formatRemaining(left) }
  }
  return { tense: 'upcoming', rosterName: target.rosterName, remainingText: formatRemaining(left) }
}
